/**
 * The Y/N flags of New-TraverseEnvironment.ps1, each optional and N by default. The one place to change their labels, groups and which are
 * taken from an earlier answer instead of being asked. The names are the script's. [CONFIRM the labels and the "taken from" mapping]
 *
 *   core      the three that matter for the core work (CreateClientFolderYN, ConfigValuesYN, FolderPermsYN)
 *   other     the rest of those that are asked
 *   derived   not asked again: the answer is the one given the first time (the add-ons on the Infrastructure step, "Use Master Login"
 *             in License management)
 */
import type { AppFlagKey } from '@/types/application'

export type FlagGroup = 'core' | 'other'

export interface AppFlag {
  key: AppFlagKey
  label: string
  group: FlagGroup
  /** Set when the flag is not asked: where its answer comes from. */
  takenFrom?: string
}

export const APP_FLAGS: readonly AppFlag[] = Object.freeze([
  { key: 'CreateClientFolderYN', label: 'Create the client folder', group: 'core' },
  { key: 'ConfigValuesYN', label: 'Set the configuration values', group: 'core' },
  { key: 'FolderPermsYN', label: 'Set the folder permissions', group: 'core' },
  { key: 'InstallSQLYN', label: 'Install SQL', group: 'other' },
  { key: 'DBSetup', label: 'Set up the databases', group: 'other' },
  { key: 'DBTemplateDeploy', label: 'Deploy the database template', group: 'other' },
  { key: 'ApplyBuildYN', label: 'Apply the build', group: 'other' },
  { key: 'InstallTravAlerts', label: 'Install Traverse Alerts', group: 'other' },
  { key: 'ShippingIntYN', label: 'Shipping integration', group: 'other' },
  { key: 'MasterLogin', label: 'Use the master login', group: 'other', takenFrom: 'License management: Use Master Login' },
  { key: 'IISApps', label: 'Create the IIS applications', group: 'other', takenFrom: 'the add-ons chosen on the Infrastructure step' },
  { key: 'InstallWMS', label: 'Install WMS', group: 'other', takenFrom: 'WMS, on the Infrastructure step' },
  { key: 'InstallAPI', label: 'Install the API portal', group: 'other', takenFrom: 'Trav API Portal and Web API Portal, on the Infrastructure step' },
  { key: 'InstallAP', label: 'Install the Admin Portal', group: 'other', takenFrom: 'Admin Portal, on the Infrastructure step' },
  { key: 'InstallCP', label: 'Install the Customer Portal', group: 'other', takenFrom: 'Customer Portal, on the Infrastructure step' },
] satisfies AppFlag[])

export const ALL_FLAG_KEYS: readonly AppFlagKey[] = APP_FLAGS.map((flag) => flag.key)
/** The flags the user is asked: not taken from an earlier answer. */
export const ASKED_FLAGS: readonly AppFlag[] = APP_FLAGS.filter((flag) => !flag.takenFrom)
/** The flags that are not asked again. */
export const DERIVED_FLAGS: readonly AppFlag[] = APP_FLAGS.filter((flag) => flag.takenFrom)

export const FLAG_GROUP_TITLES: Readonly<Record<FlagGroup, string>> = {
  core: 'Core set-up',
  other: 'Other components',
}
